import React, { useState, useMemo } from "react";
import styles from "./overdueValueByCourseTable.module.css";
import InfoIcon from "../../../assets/icons/info-icon.svg";
import SearchIcon from "../../../assets/icons/search-icon.svg";
import { formatNumberToCurrency } from "../../../utils/formatter";

const OverdueValueByCourseTable = ({ overdueData = [], showAll = false }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [showPopover, setShowPopover] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");

  const validOverdueData = useMemo(() => {
    return overdueData
      .filter((course) =>
        (course.courseTitle || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        const cmp = a.issuedValue - b.issuedValue;
        return sortOrder === "asc" ? cmp : -cmp;
      });
  }, [overdueData, searchTerm, sortOrder]);

  const totalPages = useMemo(
    () => Math.ceil(validOverdueData.length / itemsPerPage),
    [validOverdueData.length, itemsPerPage]
  );

  const currentTableData = useMemo(() => {
    if (showAll) return validOverdueData;
    const start = (currentPage - 1) * itemsPerPage;
    return validOverdueData.slice(start, start + itemsPerPage);
  }, [currentPage, itemsPerPage, validOverdueData, showAll]);

  const handlePageClick = (page) => setCurrentPage(page);

  const emptyRows = showAll ? [] : Array(Math.max(0, itemsPerPage - currentTableData.length)).fill(null);
  const hasNoData = validOverdueData.length === 0;

  return (
    <div className={styles.tableContainer}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>Maior Valor em Atraso por Curso</h2>
          <div style={{ position: "relative" }}>
            <img
              src={InfoIcon}
              alt="Informação"
              onClick={() => setShowPopover(!showPopover)}
              style={{
                width: "14px",
                height: "14px",
                cursor: "pointer",
                verticalAlign: "middle",
                display: "inline-block",
              }}
            />
            {showPopover && (
              <div className={styles.popover}>
                <p style={{ margin: 0, fontSize: "12px" }}>
                  Tabela que exibe os cursos com maior valor em atraso, permitindo identificar quais cursos concentram maior volume de inadimplência. Os dados podem ser filtrados por nome do curso e ordenados por valor.
                </p>
                <button
                  onClick={() => setShowPopover(false)}
                  className={styles.closeBtn}
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.headerRight}>
          <div className={styles.searchWrapper}>
            <img src={SearchIcon} alt="Buscar" className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Pesquisar curso..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className={styles.searchInput}
            />
          </div>

          <select
            className={styles.orderWrapper}
            value={sortOrder}
            onChange={(e) => {
              setSortOrder(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="desc">Maior Valor em Atraso</option>
            <option value="asc">Menor Valor em Atraso</option>
          </select>
        </div>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Título do Curso</th>
            <th>Valor em Atraso</th>
          </tr>
        </thead>
        <tbody>
          {hasNoData ? (
            <tr>
              <td colSpan="2" className={styles.noData}>
                Nenhum curso encontrado
              </td>
            </tr>
          ) : (
            <>
              {currentTableData.map((course, idx) => (
                <tr key={idx}>
                  <td>{course.courseTitle}</td>
                  <td>{formatNumberToCurrency(course.issuedValue)}</td>
                </tr>
              ))}
              {emptyRows.map((_, idx) => (
                <tr key={`empty-${idx}`}>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>

      {!showAll && totalPages > 1 && (
        <div className={styles.pagination}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={page === currentPage ? styles.activePage : ""}
              onClick={() => handlePageClick(page)}
            >
              {page}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default OverdueValueByCourseTable;
